import { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  BottomNavigation,
  BottomNavigationAction,
  useTheme,
  Box,
  Drawer,
  Typography,
  ButtonBase,
  Stack,
} from '@mui/material';
import { useAuth } from 'providers/AuthProvider';
import { useSettingsContext } from 'providers/SettingsProvider';
import {
  getCoupleSitemap,
  getGuestSitemap,
  getCoupleBottomNavOrder,
  COUPLE_BOTTOM_NAV_MAX,
} from 'routes/wedding-sitemap';
import sitemap from 'routes/sitemap';
import { SubMenuItem } from 'routes/sitemap';
import IconifyIcon from 'components/base/IconifyIcon';
import VibrantBackground from 'components/common/VibrantBackground';
import { calculateDaysUntil } from 'lib/dashboard-utils';
import { hasInvitationProduct } from 'utils/plan';

const GROUPED_PATH_NAMES = new Set(['couple-upload', 'couple-wishes', 'couple-quiz']);

const MORE_TAB_VALUE = '__more__';

const MobileBottomNav = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { sessionUser } = useAuth();
  const {
    config: { navColor },
  } = useSettingsContext();
  const [moreOpen, setMoreOpen] = useState(false);
  const isVibrant = navColor === 'vibrant';

  const activeSitemap = useMemo(() => {
    if (!sessionUser) return sitemap;

    const eventId = sessionUser.eventId;

    if (sessionUser.role === 'couple')
      return getCoupleSitemap(eventId, sessionUser.plan ?? 'free', sessionUser.eventType);
    if (sessionUser.role === 'uploader') {
      return getGuestSitemap(eventId, sessionUser.plan ?? 'free', sessionUser.eventType);
    }

    return sitemap;
  }, [sessionUser]);

  // Couples get a lifecycle-aware bottom bar: planning destinations before the
  // event (Guests, Seating), keepsakes after it (Album, Wishes, Quiz). The
  // event date and quiz flag are stamped onto the session from /couple/me;
  // until the background refresh lands they default to the pre-event order,
  // which then self-corrects. Guests keep the static split below.
  const couplePrimaryOrder = useMemo(() => {
    if (sessionUser?.role !== 'couple') return null;
    const isPostEvent = sessionUser.weddingDate
      ? calculateDaysUntil(sessionUser.weddingDate) < 0
      : false;
    return getCoupleBottomNavOrder(
      isPostEvent,
      sessionUser.hasQuiz ?? false,
      hasInvitationProduct(sessionUser.plan),
    );
  }, [sessionUser]);

  // Flatten all items from menus, then split into primary tabs + grouped (More) items
  const { primaryItems, groupedItems } = useMemo(() => {
    const all = activeSitemap.flatMap((menu) => menu.items || []);

    if (couplePrimaryOrder) {
      // Order the existing destinations by the phase priority list, appending
      // any item the list doesn't mention (safety) in sitemap order. The first
      // COUPLE_BOTTOM_NAV_MAX become primary tabs; the rest go to "More".
      const byPath = new Map(all.map((item) => [item.pathName, item]));
      const seen = new Set<string>();
      const ordered: SubMenuItem[] = [];
      for (const pathName of couplePrimaryOrder) {
        const item = byPath.get(pathName);
        if (item?.pathName && !seen.has(item.pathName)) {
          ordered.push(item);
          seen.add(item.pathName);
        }
      }
      for (const item of all) {
        if (item.pathName && !seen.has(item.pathName)) ordered.push(item);
      }
      return {
        primaryItems: ordered.slice(0, COUPLE_BOTTOM_NAV_MAX),
        groupedItems: ordered.slice(COUPLE_BOTTOM_NAV_MAX),
      };
    }

    const primary: SubMenuItem[] = [];
    const grouped: SubMenuItem[] = [];
    for (const item of all) {
      if (item.pathName && GROUPED_PATH_NAMES.has(item.pathName)) {
        grouped.push(item);
      } else {
        primary.push(item);
      }
    }
    return { primaryItems: primary, groupedItems: grouped };
  }, [activeSitemap, couplePrimaryOrder]);

  const hasMoreTab = groupedItems.length > 0;

  // Compute the active value: pathName of the matched primary item, or MORE_TAB_VALUE if a
  // grouped item matches the current route. Falls back to the first primary item.
  const activeValue = useMemo(() => {
    const matchItem = (item: SubMenuItem) => {
      if (!item.path) return false;
      if (location.pathname === item.path) return true;
      if (item.path !== '/' && location.pathname.startsWith(item.path)) return true;
      return false;
    };

    const primaryMatch = primaryItems.find(matchItem);
    if (primaryMatch) return primaryMatch.pathName;

    if (hasMoreTab && groupedItems.some(matchItem)) return MORE_TAB_VALUE;

    return primaryItems[0]?.pathName ?? MORE_TAB_VALUE;
  }, [location.pathname, primaryItems, groupedItems, hasMoreTab]);

  // Close the bottom sheet automatically when navigating away
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  if (primaryItems.length === 0 && !hasMoreTab) return null;

  const handleChange = (_: unknown, newValue: string) => {
    if (newValue === MORE_TAB_VALUE) {
      setMoreOpen(true);
      return;
    }
    const target = primaryItems.find((item) => item.pathName === newValue);
    if (target?.path) navigate(target.path);
  };

  const handleGroupedItemClick = (item: SubMenuItem) => {
    setMoreOpen(false);
    if (item.path) navigate(item.path);
  };

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar,
          display: { xs: 'block', md: 'none' },
          pb: 'env(safe-area-inset-bottom)',
          bgcolor: isVibrant ? 'transparent' : 'background.paper',
          borderTop: '1px solid',
          borderColor: isVibrant ? 'transparent' : 'divider',
          overflow: 'hidden',
        }}
      >
        {isVibrant && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              transform: 'scaleY(-1)',
              pointerEvents: 'none',
            }}
          >
            <VibrantBackground position="top" />
          </Box>
        )}
        <BottomNavigation
          showLabels
          value={activeValue}
          onChange={handleChange}
          sx={{
            position: 'relative',
            height: 64,
            backgroundColor: 'transparent',
            '& .MuiBottomNavigationAction-root': {
              // Tabs share the bar equally and may shrink below their label;
              // a long label («Προσκεκλημένοι») then ends in an ellipsis
              // instead of running into its neighbour.
              minWidth: 0,
              padding: '6px 4px 8px',
              color: 'text.secondary',
              transition: 'color 0.2s ease',
              '&.Mui-selected': {
                color: 'primary.main',
                '& .MuiBottomNavigationAction-label': {
                  fontWeight: 700,
                },
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem',
                fontWeight: 500,
                mt: 0.5,
                textTransform: 'none',
                transition: 'font-weight 0.2s ease',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            },
          }}
        >
          {primaryItems.map((item) => (
            <BottomNavigationAction
              key={item.pathName}
              value={item.pathName}
              label={t(item.key || item.name)}
              icon={
                item.customIcon ? (
                  <Box
                    component="img"
                    src={item.customIcon}
                    sx={{ width: 24, height: 24, objectFit: 'contain', ...item.iconSx }}
                  />
                ) : (
                  <IconifyIcon
                    icon={item.icon || 'material-symbols:circle'}
                    sx={{ fontSize: 24, ...(item.iconSx || {}) }}
                  />
                )
              }
            />
          ))}
          {hasMoreTab && (
            <BottomNavigationAction
              key={MORE_TAB_VALUE}
              value={MORE_TAB_VALUE}
              label={t('nav.menu.more')}
              icon={<IconifyIcon icon="solar:menu-dots-bold-duotone" sx={{ fontSize: 24 }} />}
            />
          )}
        </BottomNavigation>
      </Box>

      <Drawer
        anchor="bottom"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            pb: 'calc(env(safe-area-inset-bottom) + 16px)',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 1,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 2,
              bgcolor: 'divider',
            }}
          />
        </Box>
        <Typography
          variant="subtitle2"
          sx={{
            px: 2.5,
            pt: 2,
            pb: 1.5,
            color: 'text.secondary',
            fontWeight: 600,
          }}
        >
          {t('nav.menu.more_sheet_title')}
        </Typography>
        <Stack direction="column" sx={{ px: 1, pb: 1 }}>
          {groupedItems.map((item) => {
            const isActive =
              item.path &&
              (location.pathname === item.path ||
                (item.path !== '/' && location.pathname.startsWith(item.path)));
            return (
              <ButtonBase
                key={item.pathName}
                onClick={() => handleGroupedItemClick(item)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  width: '100%',
                  px: 1.5,
                  py: 1.5,
                  borderRadius: 1.5,
                  gap: 2,
                  color: isActive ? 'primary.main' : 'text.primary',
                  bgcolor: isActive ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {item.customIcon ? (
                  <Box
                    component="img"
                    src={item.customIcon}
                    sx={{ width: 24, height: 24, objectFit: 'contain', ...item.iconSx }}
                  />
                ) : (
                  <IconifyIcon
                    icon={item.icon || 'material-symbols:circle'}
                    sx={{ fontSize: 24, ...(item.iconSx || {}) }}
                  />
                )}
                <Typography variant="body1" sx={{ fontWeight: isActive ? 700 : 500 }}>
                  {t(item.key || item.name)}
                </Typography>
              </ButtonBase>
            );
          })}
        </Stack>
      </Drawer>
    </>
  );
};

export default MobileBottomNav;
